// --- React libraries ---
import { useState, useCallback, useEffect } from "react";
import { Plus } from "lucide-react";

// --- shared components ---
// import { TimekeepingApplet } from "../../applets/timekeeping/TimeKeepingApplet/TimekeepingApplet";
import { getTimeEntrySheetId } from "../../constants/timekeeping";
import { SheetView } from "../../views/grid/components/SheetView";
import { TabBar, type Tab } from "../../views/grid/components/TabBar";
import { TimekeepingDashboard } from "./TimekeepingDashboard";
import {
  ModuleToolbar,
  ModuleToolbarProvider,
  ToolbarButton,
  type ToolbarTabType,
} from "../../components/module";
import RemoteAppletLoader from "../../components/shell/RemoteAppletLoader";
import ErrorBoundary from "../../components/ErrorBoundary";


// --- hooks ---
import { useSheetData, queryKeys } from "../../hooks/useSheetData";
import { useAuth } from "../../contexts/AuthContext";
import { useQuery, useQueryClient } from '@tanstack/react-query';

// --- styles ---
import "../../views/grid/styles/tabBar.css";

type TabId = "applet" | "entries" | "dashboard";

/** Map TabId to ToolbarTabType for contextual icons */
const TAB_TYPE_MAP: Record<TabId, ToolbarTabType> = {
  applet: "applet",
  entries: "grid",
  dashboard: "dashboard",
};


export function TimekeepingModule() {
  const [activeTab, setActiveTab] = useState<TabId>("applet");
  const [isPresenting, setIsPresenting] = useState(false);
  const domainId = localStorage.getItem("swm-current-workspace-id");
  const sheetId = getTimeEntrySheetId(domainId);

  const sheetData = useSheetData(sheetId);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  console.log('domainId:', domainId);  // Add this

  // Fetch the timekeeping module with applet
  const { data: module } = useQuery({
    queryKey: ['module', domainId, 'timekeeping'],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/modules?tenantId=${domainId}`, {
        credentials: 'include'
      });
      const { modules } = await response.json()
      console.log('Fetched modules:', modules);  // Add this
      const found = modules.find((m: any) => m.name === 'Timekeeping' && (m.domainId === domainId || m.domainId === null));
      console.log('Found module:', found);


      return found;
    },
    enabled: !!domainId,
  });

  // Get definitionId from the module
  console.log("Applets found in Module", module?.applets?.[0]);
  const definitionId = module?.applets?.[0]?.definitionId;
  console.log(definitionId);

  // force update when domain changes
  useEffect(() => {
    // This effect is mainly to ensure we have the latest domainId if it changes outside this component
    // though React key on parent usually handles this better.
  }, [domainId]);

  // Listen for fullscreen changes to sync present state
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsPresenting(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Transform to TabBar's expected format
  const tabs: Tab[] = [
    { id: "applet", name: "Applet", isActive: activeTab === "applet" },
    { id: "entries", name: "Time Entries", isActive: activeTab === "entries" },
    { id: "dashboard", name: "Dashboard", isActive: activeTab === "dashboard" },
  ];

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId as TabId);
  };

  // Toolbar action handlers (AC-6.1.4)
  const handleRefresh = useCallback(() => {
    // Trigger a data refresh for the current sheet
    queryClient.invalidateQueries({ queryKey: queryKeys.rows(sheetId) });
  }, [queryClient, sheetId]);

  const handleSettings = useCallback(() => {
    // Dispatch event to open TimekeepingApplet settings modal (Story 6.2)
    window.dispatchEvent(new CustomEvent("timekeeping:openSettings"));
  }, []);

  const handleAddManualEntry = useCallback(() => {
    // Dispatch event to open TimekeepingApplet manual entry modal
    window.dispatchEvent(new CustomEvent("timekeeping:addManualEntry"));
  }, []);

  const handlePresent = useCallback(() => {
    // Dispatch event to toggle present mode in dashboard
    window.dispatchEvent(new CustomEvent("timekeeping:present"));
    // Enter fullscreen mode (reuse PresentModeToolbar pattern)
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error("Fullscreen request failed:", err);
      });
    }
  }, []);

  // Get toolbar tab type for contextual icons
  const activeTabType = TAB_TYPE_MAP[activeTab];

  return (
    <ModuleToolbarProvider activeTabType={activeTabType}>
      <div className="h-full flex flex-col">
        {/* Unified Module Toolbar - Hidden in present mode */}
        {!isPresenting && (
          <ModuleToolbar
            title="Timekeeping"
            activeTabType={activeTabType}
            onRefresh={handleRefresh}
            onSettings={handleSettings}
            onPresent={handlePresent}
          >
            {activeTab === "applet" && (
              <ToolbarButton
                icon={<Plus className="w-5 h-5" />}
                label="Add Manual Entry"
                onClick={handleAddManualEntry}
              />
            )}
          </ModuleToolbar>
        )}

        {/* Tab Content - CSS-based hiding preserves component state (no re-fetch on tab switch) */}
        <div className="flex-1 overflow-hidden relative">
          <div
            className={`absolute inset-0 overflow-auto ${
              activeTab === "applet" ? "" : "hidden"
            }`}
          >
            
            {/* Pass props to TimekeepingAplet */}
            {sheetData.isLoading || !user ? (
              <div>Loading Timekeeping Applet...</div>
            ) : (

            <ErrorBoundary>
              <RemoteAppletLoader
                definitionId={definitionId}
                sheetData={sheetData}
                user={user}
                domainId={domainId}
              />
            </ErrorBoundary>

            )}
          </div>
          <div
            className={`absolute inset-0 overflow-auto ${
              activeTab === "entries" ? "" : "hidden"
            }`}
          >
            <SheetView sheetId={sheetId} hideTabBar hideToolbar />
          </div>
          <div
            className={`absolute inset-0 overflow-auto ${
              activeTab === "dashboard" ? "" : "hidden"
            }`}
          >
            <TimekeepingDashboard />
          </div>
        </div>

        {/* Bottom Tab Bar - Hidden in present mode */}
        {!isPresenting && <TabBar tabs={tabs} onTabClick={handleTabClick} />}
      </div>
    </ModuleToolbarProvider>
  );
}

export default TimekeepingModule;
